import { useState } from "react";
import { Gift, Send, CheckCircle2 } from "lucide-react";
import { Api } from "../lib/api";

const PRIZES = [
  { tier: "Bronze", invites: 3, prize: "Two complimentary Lawn passes" },
  { tier: "Silver", invites: 6, prize: "Premium Picnic table for four" },
  { tier: "Gold", invites: 10, prize: "Signature dinner experience for two" },
];

export default function InvitePage() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [done, setDone] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    setErr(null);
    try {
      const res = await Api.createInvite({ inviter_email: email, inviter_name: name });
      setDone(res);
    } catch (ex) {
      setErr(ex.response?.data?.detail || ex.message || "Failed to send invite");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="container-x py-12 md:py-20" data-testid="invite-page">
      <p className="eyebrow">Invite &amp; win</p>
      <h1 className="heading-xl mt-2">
        Bring friends. <span className="italic-accent">Win the day.</span>
      </h1>
      <p className="body-lead mt-3 max-w-xl">
        Send an invite to people who&apos;d love an island day with us — the more friends who book, the
        more rewards you unlock.
      </p>

      <div className="mt-10 grid md:grid-cols-2 gap-8">
        {done ? (
          <div className="card p-7 text-center" data-testid="invite-success">
            <div className="w-14 h-14 rounded-full bg-coral/10 text-coral flex items-center justify-center mx-auto mb-3">
              <CheckCircle2 size={26} />
            </div>
            <p className="font-serif text-2xl text-navy">Your invite code is ready!</p>
            <p className="font-serif text-4xl text-coral mt-3 tracking-widest">{done.code}</p>
            <p className="text-grey text-sm mt-3">
              Share this code with friends. Each booking they make counts towards your rewards.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="card p-7 space-y-3" data-testid="invite-form">
            <p className="eyebrow">Get your unique invite code</p>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="Your name"
              className="w-full rounded-xl border border-[#EFE7D2] p-3 bg-cream/40"
              data-testid="invite-name"
            />
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              type="email"
              placeholder="Your email"
              className="w-full rounded-xl border border-[#EFE7D2] p-3 bg-cream/40"
              data-testid="invite-email"
            />
            {err && (
              <p className="text-red text-sm" data-testid="invite-error">
                {err}
              </p>
            )}
            <button
              disabled={loading}
              className="btn-primary w-full justify-center disabled:opacity-50"
              data-testid="invite-submit"
            >
              {loading ? "Sending…" : "Get my invite code"} <Send size={16} />
            </button>
            <p className="text-xs text-grey">
              We&apos;ll give you a unique code to share. Track invites in your inbox.
            </p>
          </form>
        )}

        <div className="space-y-3">
          {PRIZES.map((p) => (
            <div key={p.tier} className="card p-5 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-coral/10 text-coral flex items-center justify-center">
                <Gift size={20} />
              </div>
              <div>
                <p className="text-[10px] font-bold tracking-widest uppercase text-coral">
                  {p.invites} invites
                </p>
                <p className="font-serif text-xl text-navy">{p.tier}</p>
                <p className="text-grey text-sm">{p.prize}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
